import { useTranslation } from 'react-i18next';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Col, Drawer, Menu, Row } from 'antd';
import classNames from 'classnames';
import { array, openid } from '@app/base';
import {
  useUserContextSelector,
  useResetTwoFactorMethod,
  useRoleBasedAccess,
  useModuleVisibility,
} from '@app/main/utils';
import { Avatar, Icon, Shortcut, confirmation, createShortcut, useMobile } from '@app/ui';
import { NotificationsAlert } from '@app/notifications/widgets/Notifications.Alert';
import css from './Layout.module.scss';
import { useSelectedNavMenuKeys } from './useSelectedNavMenuKeys';
import { InfoCircleOutlined, MenuOutlined, MessageOutlined } from '@ant-design/icons';
import { useCallback, useState } from 'react';
import { chatifyApi } from '@webinex/chatify';
import { ItemType } from 'antd/es/menu/interface';
import { LogoUrl } from '@app/common/assets';

function ChatNavItem() {
  const { data: chats } = chatifyApi.useGetChatListQuery();
  const { isMobile } = useMobile();
  const { t } = useTranslation();
  let count = 0;
  chats?.forEach((x) => (count += x.totalUnreadCount));

  return (
    <Shortcut combo={createShortcut('c')}>
      <NavLink to="/chat">
        {isMobile ? <span>{t('chat')}</span> : <MessageOutlined />}
        <span className={classNames(css.count, { [css.mobile]: isMobile })}>{count}</span>
      </NavLink>
    </Shortcut>
  );
}

type MenuItemType = ItemType & { showTablet?: boolean };

function useNavMenu(): { navMenu: JSX.Element; mobileNavMenu: JSX.Element } {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [name, avatar] = useUserContextSelector((x) => [x.name, x.avatar]);
  const hasRole = useUserContextSelector((x) => x.hasRole);
  const resetTwoFactorMethod = useResetTwoFactorMethod();
  const { isMobile, isTablet } = useMobile();
  const roleAccessMap = useRoleBasedAccess();
  const { isSchedulingVisible } = useModuleVisibility();

  const showSignOutConfirmationDialog = () => {
    confirmation.show({
      title: t('logoutConfirmation.title'),
      body: t('logoutConfirmation.body'),
      onConfirm: () => openid.signout(),
    });
  };

  const items: MenuItemType[] = array.withoutDisabled([
    roleAccessMap.DashboardViewRoles && {
      key: '/dashboard',
      label: (
        <Shortcut combo={createShortcut('d')}>
          <NavLink data-test="navPanel-dashboard" to="/dashboard">
            {t('dashboards.title')}
          </NavLink>
        </Shortcut>
      ),
    },
    roleAccessMap.PatientListAccessRoles && {
      key: '/patient',
      label: (
        <Shortcut combo={createShortcut('p')}>
          <NavLink data-test="navPanel-patient" to="/patient">
            {t('patients.title')}
          </NavLink>
        </Shortcut>
      ),
    },
    isSchedulingVisible &&
      roleAccessMap.ScheduleViewAccessRoles && {
        key: '/appointment/schedule',
        label: (
          <Shortcut combo={createShortcut('a')}>
            <NavLink to="/appointment/schedule">{t('appointments.title')}</NavLink>
          </Shortcut>
        ),
      },
    hasRole('PATIENT') && {
      key: '/my-health',
      label: (
        <Shortcut combo={createShortcut('h')}>
          <NavLink to="/my-health">{t('nav.myHealth')}</NavLink>
        </Shortcut>
      ),
    },
    hasRole('GLOBAL_ADMIN') && {
      key: '/organizations',
      className: 'nav-icon-link',
      label: (
        <Shortcut combo={createShortcut('o')}>
          <NavLink to="/organizations">{t('organizations.title')}</NavLink>
        </Shortcut>
      ),
    },
    roleAccessMap.EncounterReportAccessRoles && {
      key: '/report',
      label: (
        <Shortcut combo={createShortcut('r')}>
          <NavLink to="/report" aria-label={t('encounters.report.title')}>
            {t('encounters.report.title')}
          </NavLink>
        </Shortcut>
      ),
    },
    roleAccessMap.AdministrationRoles && {
      key: '/administration',
      className: 'nav-icon-link',
      label: (
        <Shortcut combo={createShortcut('g')}>
          <NavLink
            data-test="navPanel-administration"
            to="/administration"
            aria-label={t('administration.title')}
          >
            {isTablet ? t('administration.title') : <Icon type="settings" />}
          </NavLink>
        </Shortcut>
      ),
    },
    {
      showTablet: true,
      key: '/notifications',
      label: <NotificationsAlert />,
    },
    roleAccessMap.ChatAccessRoles && {
      showTablet: true,
      key: '/chat',
      className: 'nav-icon-link',
      label: <ChatNavItem />,
    },
    {
      key: '/info',
      className: 'nav-icon-link',
      label: isMobile || isTablet ? t('nav.about') : <InfoCircleOutlined />,
      children: [
        {
          key: '/about-us',
          onClick: () => navigate('/about-us'),
          label: t('aboutUs'),
        },
        {
          key: '/terms-and-conditions',
          onClick: () => navigate('/terms-and-conditions'),
          label: t('terms'),
        },
      ],
    },
    {
      showTablet: true,
      key: '/user-actions',
      className: 'nav-icon-link',
      label: isMobile ? (
        t('nav.profile')
      ) : (
        <div data-test="navPanel-user-avatar" aria-label={t('nav.profileAvatar', { name })}>
          <Avatar className="block" size={32} reference={avatar} alt={name} />
        </div>
      ),
      children: array.withoutDisabled([
        {
          key: '/me',
          label: (
            <NavLink data-test="profile-btn" to="/me">
              {t('nav.myProfile')}
            </NavLink>
          ),
        },
        isSchedulingVisible &&
          hasRole('PERFORMER') && {
            key: '/me/availability',
            onClick: () => navigate('/me/availability'),
            label: t('availabilities.title'),
          },
        {
          key: '/me/appearance',
          label: (
            <NavLink data-test="nav-appearance-btn" to="/me/appearance">
              {t('appearance.title')}
            </NavLink>
          ),
        },
        {
          key: '/change-password',
          label: (
            <NavLink data-test="nav-change-password-btn" to="/change-password">
              {t('nav.changePassword')}
            </NavLink>
          ),
        },
        {
          key: 'reset-mfa',
          onClick: resetTwoFactorMethod,
          label: <div data-test="nav-reset-mfa-btn">{t('users.twoFactorMethodReset.button')}</div>,
        },
        {
          key: '/logout',
          onClick: showSignOutConfirmationDialog,
          label: <div data-test="nav-logout-btn">{t('nav.logout')}</div>,
        },
      ]),
    },
  ]);

  const selectedKeys = useSelectedNavMenuKeys(items);

  return {
    navMenu: (
      <Menu
        selectedKeys={selectedKeys}
        className="nav"
        mode={isMobile ? 'inline' : 'horizontal'}
        disabledOverflow
        items={isTablet && !isMobile ? items.filter((x) => x.showTablet) : items}
      />
    ),
    mobileNavMenu: (
      <Menu
        selectedKeys={selectedKeys}
        className="nav"
        mode="inline"
        disabledOverflow
        items={!isMobile ? items.filter((x) => !x.showTablet) : items}
      />
    ),
  };
}

export function NavPanel() {
  const { navMenu, mobileNavMenu } = useNavMenu();
  const { isMobile, isTablet } = useMobile();
  const { t } = useTranslation();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleMobileMenuOpen = useCallback(() => setMobileMenuOpen(true), []);
  const handleMobileMenuClose = useCallback(() => setMobileMenuOpen(false), []);

  return (
    <Row justify="space-between" gutter={[0, 0]} wrap={false}>
      <Col>
        <Row className={css.menuLogoHolder}>
          {isTablet && (
            <Row className={css.mobileMenuHolder}>
              <MenuOutlined onClick={handleMobileMenuOpen} />
              <Drawer title="Menu" placement="left" onClose={handleMobileMenuClose} open={mobileMenuOpen}>
                {mobileNavMenu}
              </Drawer>
            </Row>
          )}
          <Link to="/" className={classNames(css.brand, 'ms-2')} aria-label={t('brand.name')}>
            <span className={css.logo}>
              <img className={css.logoImage} src={LogoUrl} alt="" />
            </span>
            <span className={css.brandName}>{t('brand.name')}</span>
          </Link>
          <Link to="/" className={classNames(css.mobileBrand, 'ms-2')} aria-label={t('brand.name')}>
            <img className={css.logoImage} src={LogoUrl} alt="" />
          </Link>
        </Row>
      </Col>
      {!isMobile && <Col>{navMenu}</Col>}
    </Row>
  );
}
